import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useAccountStores } from "../api/get-account-stores";
import { useUpdateStoreDelinquent } from "../api/update-store-delinquent";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { getBillingRestrictionStageLabel } from "../billingRestriction";

export default function AccountDetailStores({ accountId }: { accountId: string }) {
    const storesQuery = useAccountStores({ accountId: accountId || '' })
    const stores = storesQuery.data
    const updateDelinquent = useUpdateStoreDelinquent()
    return (
        <Card className='h-full'>
            <CardHeader>
                <CardTitle>Stores</CardTitle>
            </CardHeader>
            <CardContent className='overflow-y-auto'>
                {storesQuery.isLoading && <div>Loading stores...</div>}
                {storesQuery.error && !storesQuery.isLoading && (
                    <div className='text-destructive'>Failed to load stores.</div>
                )}
                {!storesQuery.isLoading && !storesQuery.error && (
                    <>
                        {Array.isArray(stores) && stores.length > 0 ? (
                            <div className='space-y-4'>
                                {stores.map((s, idx) => {
                                    const businessHours = (() => {
                                        try { return s?.businessHours ? JSON.parse(s.businessHours) : null } catch { return null }
                                    })()
                                    const billingRestrictionLabel = getBillingRestrictionStageLabel(s.billingRestriction)
                                    return (
                                        <div key={s?.id ?? s?.storeId ?? idx} className='rounded border p-3 bg-white'>
                                            <div className='flex flex-wrap items-center justify-between gap-2'>
                                                <div className='font-semibold'>{s?.friendlyFFLName || s?.legalFFLName || s?.fflNumber || 'Store'}</div>
                                                <div className='flex items-center gap-2'>
                                                    {s?.isDisabled ? <Badge variant='destructive'>Disabled</Badge> : <Badge>Active</Badge>}
                                                    {s?.isDelinquent && <Badge variant='destructive'>Manual delinquency</Badge>}
                                                    {billingRestrictionLabel && (
                                                        <Badge variant='destructive'>
                                                            Billing: {billingRestrictionLabel}
                                                            {s.billingRestriction?.isAccountScoped ? ' (account)' : ' (store)'}
                                                        </Badge>
                                                    )}
                                                    {typeof s?.usersCount === 'number' && (
                                                        <Badge variant='outline'>Users: {s.usersCount}</Badge>
                                                    )}
                                                    {typeof s?.documentsCount === 'number' && (
                                                        <Badge variant='outline'>Docs: {s.documentsCount}</Badge>
                                                    )}
                                                </div>
                                            </div>

                                            <div className='mt-3 grid gap-3 md:grid-cols-2'>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>FFL</div>
                                                    <div className='text-sm'>{s?.fflNumber}</div>
                                                    <div className='text-xs text-muted-foreground'>First 3: {s?.fflFirstThreeDigits} • Last 5: {s?.fflLastFiveDigits}</div>
                                                </div>
                                                <div className='text-sm'>
                                                    <div className='text-sm text-muted-foreground'>FFL Data</div>
                                                    <div>{s?.varianceName || '—'}</div>
                                                </div>
                                            </div>

                                            <div className='mt-3 grid gap-3 md:grid-cols-2'>
                                                <div>
                                                    <div className='text-sm font-medium'>Premises Address</div>
                                                    <div className='text-sm'>{[s?.premiseStreet, s?.streetAddress2].filter(Boolean).join(' ')}</div>
                                                    <div className='text-sm text-muted-foreground'>
                                                        {[s?.premiseCity, s?.premiseState, s?.premiseZipCode].filter(Boolean).join(', ')}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className='text-sm font-medium'>Mailing Address</div>
                                                    <div className='text-sm'>{s?.mailingStreet}</div>
                                                    <div className='text-sm text-muted-foreground'>
                                                        {[s?.mailingCity, s?.mailingState, s?.mailingZipCode].filter(Boolean).join(', ')}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className='mt-3 grid gap-3 md:grid-cols-2'>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Email</div>
                                                    <div className='text-sm'>{s?.email || '—'}</div>
                                                </div>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Phone</div>
                                                    <div className='text-sm'>{s?.phone || '—'}</div>
                                                </div>
                                            </div>

                                            <div className='mt-3 grid gap-3 md:grid-cols-3'>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Audit Status</div>
                                                    <div className='text-sm'>{s?.auditStatus ?? '—'}</div>
                                                </div>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Delete Docs On Expiration</div>
                                                    <div className='text-sm'>{s?.shouldDeleteDocumentsOnExpiration ? 'Yes' : 'No'}</div>
                                                </div>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Disabled</div>
                                                    <div className='text-sm'>{s?.isDisabled ? 'Yes' : 'No'}</div>
                                                </div>
                                            </div>

                                            <div className='mt-3 grid gap-3 md:grid-cols-3'>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>LOA Issue</div>
                                                    <div className='text-sm'>{s?.loaIssueDate ?? '—'}</div>
                                                </div>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>LOA Expiration</div>
                                                    <div className='text-sm'>{s?.loaExpirationDate ?? '—'}</div>
                                                </div>
                                                <div>
                                                    <div className='text-sm text-muted-foreground'>Expiration</div>
                                                    <div className='text-sm'>{s?.expirationDate ?? '—'}</div>
                                                </div>
                                            </div>

                                            <div className='mt-3'>
                                                <div className='text-sm text-muted-foreground mb-2'>Business Hours</div>
                                                {businessHours ? (
                                                    <ul className='text-sm space-y-1'>
                                                        {[
                                                            'sunday',
                                                            'monday',
                                                            'tuesday',
                                                            'wednesday',
                                                            'thursday',
                                                            'friday',
                                                            'saturday',
                                                        ].map((day) => {
                                                            const d = (businessHours as any)[day] || {}
                                                            const isActive = !!d.isActive
                                                            const fmt = (v: string) => {
                                                                if (!v) return '—'
                                                                const dt = new Date(v)
                                                                return isNaN(dt.getTime())
                                                                    ? '—'
                                                                    : dt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                                                            }
                                                            const start = fmt(d.startTime)
                                                            const end = fmt(d.endTime)
                                                            const label = day.charAt(0).toUpperCase() + day.slice(1)
                                                            return (
                                                                <li key={day} className='flex items-center justify-between'>
                                                                    <span className='font-medium'>{label}</span>
                                                                    <span className='text-muted-foreground'>
                                                                        {isActive ? `${start} – ${end}` : 'Closed'}
                                                                    </span>
                                                                </li>
                                                            )
                                                        })}
                                                    </ul>
                                                ) : (
                                                    <div className='text-sm text-muted-foreground'>—</div>
                                                )}
                                            </div>

                                            {billingRestrictionLabel && (
                                                <div className='flex items-center justify-between border rounded-md p-2 mt-3'>
                                                    <div>
                                                        <div className='text-sm font-medium'>Billing restriction</div>
                                                        <div className='text-xs text-muted-foreground'>
                                                            Read-only; managed automatically through billing
                                                        </div>
                                                    </div>
                                                    <Badge variant='destructive'>{billingRestrictionLabel}</Badge>
                                                </div>
                                            )}

                                            <div className='flex items-center justify-between border rounded-md p-2 mt-3'>
                                                <div>
                                                    <div className='text-sm font-medium'>Manual delinquency</div>
                                                    <div className='text-xs text-muted-foreground'>
                                                        Manually delinquent stores cannot upload new 4473s
                                                    </div>
                                                </div>
                                                <Switch
                                                    checked={s?.isDelinquent === true}
                                                    onCheckedChange={(checked) => updateDelinquent.mutate({ accountId, storeId: s.id, isDelinquent: checked })}
                                                    disabled={updateDelinquent.isPending}
                                                />
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        ) : (
                            <div className='italic text-muted-foreground'>No stores</div>
                        )}
                    </>
                )}
            </CardContent>
        </Card>
    )
}